// src/stores/modules/users.js
import UserService from '../../services/users/UserService'
import ApiService from '../../services/api'

export default {
  namespaced: true,
  state: {
    users: [],
    currentUser: null,
    loading: false,
    error: null,
    pagination: {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1
    },
    // Add impersonation state
    isImpersonating: false,
    originalUser: null,
    impersonatedUser: null
  },

  mutations: {
    SET_USERS(state, users) {
      state.users = users
    },

    SET_CURRENT_USER(state, user) {
      state.currentUser = user
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_PAGINATION(state, pagination) {
      state.pagination = pagination
    },

    ADD_USER(state, user) {
      state.users.push(user)
    },

    UPDATE_USER(state, updatedUser) {
      const index = state.users.findIndex(user => user.id === updatedUser.id)
      if (index !== -1) {
        state.users.splice(index, 1, updatedUser)
      }
    },

    REMOVE_USER(state, userId) {
      state.users = state.users.filter(user => user.id !== userId)
    },

    // Add impersonation mutations
    SET_IMPERSONATION(state, { isImpersonating, originalUser, impersonatedUser }) {
      state.isImpersonating = isImpersonating
      state.originalUser = originalUser
      state.impersonatedUser = impersonatedUser

      // When impersonating, the current user should be the impersonated user
      if (isImpersonating && impersonatedUser) {
        state.currentUser = impersonatedUser
      }
    },

    CLEAR_IMPERSONATION(state) {
      state.isImpersonating = false
      state.originalUser = null
      state.impersonatedUser = null
    }
  },

  actions: {
    async fetchUsers({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.getUsers(params)

        if (response.Success) {
          const { data, meta } = response.Data

          commit('SET_USERS', data || response.Data)
          if (meta) {
            commit('SET_PAGINATION', {
              currentPage: meta.current_page,
              perPage: meta.per_page,
              total: meta.total,
              lastPage: meta.last_page
            })
          }

          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to fetch users')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch users')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchUser({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.getUser(id)

        if (response.Success) {
          commit('SET_CURRENT_USER', response.Data)
          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to fetch user')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch user')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Add this new action to load the current authenticated user
    async loadCurrentAuthenticatedUser({ commit }) {
      try {
        commit('SET_LOADING', true)

        // Get current authenticated user
        const response = await ApiService.get('/auth/user')
        const userData = response?.data || response?.Data?.data

        if (userData?.user) {
          // Check if this is an impersonation session
          if (userData.is_impersonating) {
            // Also check impersonation status for full details
            const impersonationResponse = await UserService.getImpersonationStatus()
            const impData = impersonationResponse?.Data?.data || impersonationResponse?.data

            if (impData?.is_impersonating) {
              commit('SET_IMPERSONATION', {
                isImpersonating: true,
                originalUser: impData.original_user,
                impersonatedUser: impData.impersonated_user
              })
              // Current user is the impersonated user
              commit('SET_CURRENT_USER', impData.impersonated_user)
            } else {
              commit('SET_CURRENT_USER', userData.user)
            }
          } else {
            commit('SET_CURRENT_USER', userData.user)
            commit('CLEAR_IMPERSONATION')
          }

          return userData.user
        }
      } catch (error) {
        console.error('Error loading authenticated user:', error)
        commit('SET_ERROR', error.message || 'Failed to load user')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Add action to check impersonation status
    async checkImpersonationStatus({ commit }) {
      try {
        const response = await UserService.getImpersonationStatus()
        const data = response?.Data?.data || response?.data

        if (data?.is_impersonating) {
          commit('SET_IMPERSONATION', {
            isImpersonating: true,
            originalUser: data.original_user,
            impersonatedUser: data.impersonated_user
          })
          // Set current user to impersonated user
          commit('SET_CURRENT_USER', data.impersonated_user)
        } else {
          commit('CLEAR_IMPERSONATION')
        }

        return data
      } catch (error) {
        console.error('Error checking impersonation status:', error)
        commit('CLEAR_IMPERSONATION')
        return null
      }
    },

    async createUser({ commit }, userData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.createUser(userData)

        if (response.Success) {
          commit('ADD_USER', response.Data)
          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to create user')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to create user')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateUser({ commit }, { id, userData }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.updateUser(id, userData)

        if (response.Success) {
          commit('UPDATE_USER', response.Data)
          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to update user')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to update user')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deleteUser({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.deleteUser(id)

        if (response.Success) {
          commit('REMOVE_USER', id)
          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to delete user')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to delete user')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async restoreUser({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.restoreUser(id)

        if (response.Success) {
          commit('UPDATE_USER', response.Data)
          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to restore user')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to restore user')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async bulkDeleteUsers({ commit }, ids) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.bulkDeleteUsers(ids)

        if (response.Success) {
          ids.forEach(id => commit('REMOVE_USER', id))
          return response
        } else {
          commit('SET_ERROR', response.Message || 'Failed to bulk delete users')
          throw new Error(response.Message)
        }
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to bulk delete users')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async exportUsers({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)

        const response = await UserService.exportUsers(params)

        if (!response.Success) {
          commit('SET_ERROR', response.Message || 'Failed to export users')
          throw new Error(response.Message)
        }

        return response
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to export users')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    }
  },

  getters: {
    users: (state) => state.users,
    currentUser: (state) => state.currentUser,
    loading: (state) => state.loading,
    error: (state) => state.error,
    pagination: (state) => state.pagination,
    totalUsers: (state) => state.users.length,
    activeUsers: (state) => state.users.filter(user => user.status === 'active'),
    inactiveUsers: (state) => state.users.filter(user => user.status === 'inactive'),

    // Add impersonation getters
    isImpersonating: (state) => state.isImpersonating,
    originalUser: (state) => state.originalUser,
    impersonatedUser: (state) => state.impersonatedUser,

    // Get the effective user role (impersonated user's role when impersonating)
    effectiveUserRole: (state) => {
      const user = state.currentUser
      if (user?.roles && user.roles.length > 0) {
        return user.roles[0].name
      }
      return null
    },

    // Check if effective user is admin
    isEffectiveAdmin: (state, getters) => {
      if (state.isImpersonating) {
        // When impersonating, use the impersonated user's role
        const role = getters.effectiveUserRole
        return false // Don't show admin menu when impersonating
      }
      const role = getters.effectiveUserRole
      return role === 'Super Admin' || role === 'EDCTP Admin' || role === 'CMS Admin'
    },

    // Check if effective user is super admin
    isEffectiveSuperAdmin: (state, getters) => {
      if (state.isImpersonating) {
        return false // Never show super admin features when impersonating
      }
      return getters.effectiveUserRole === 'Super Admin'
    }
  }
}
